/**
 * CLEANING / SERVERS (F5-residual): SHIFT roster for cleaning. A group of
 * category `cleaning` creates shifts (by date and venue) and assigns people,
 * with a done/pending status. The ROTATION spreads the group's members across
 * pending shifts equitably (pure logic `rotateRoster`). Consumables live in
 * F3's inventory (church scope). Capability `cleaning-roster`, GROUP scope —
 * gated with `useGrants()`.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  assignmentsOfShift,
  getCleaningAssignmentsQuery,
  getCleaningShiftsQuery,
  getGroupMembersQuery,
  getVenuesQuery,
  isShiftDone,
  nextInRotation,
  patchRowVerified,
  queryKeys,
  rotateRoster,
  upcomingShifts,
} from "@agape/shared";
import {
  type ChurchRecord,
  cleaning_assignment,
  cleaning_shift,
  type CleaningAssignmentRecord,
  type CleaningShiftRecord,
  eq,
  type GroupMemberRecord,
  type PersonRecord,
  type VenueRecord,
} from "@agape/powersync";
import { CheckCircle2, Circle, RefreshCw, Sparkles, Trash2, UserPlus } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function CleaningSection(props: {
  groupId: string;
  church: ChurchRecord | undefined;
  people: readonly PersonRecord[];
}) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const shifts = useDomainQuery<CleaningShiftRecord>(
    (uid) => queryKeys.domain.cleaningShifts(uid),
    (db) => getCleaningShiftsQuery(db),
  );
  const assignments = useDomainQuery<CleaningAssignmentRecord>(
    (uid) => queryKeys.domain.cleaningAssignments(uid),
    (db) => getCleaningAssignmentsQuery(db),
  );
  const members = useDomainQuery<GroupMemberRecord>(
    (uid) => queryKeys.domain.groupMembers(uid),
    (db) => getGroupMembersQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );

  const scope = () => [props.groupId, props.church?.id];
  const canWrite = () => grants.can(scope(), "cleaning-roster", "write");

  const myShifts = createMemo(() =>
    upcomingShifts((shifts.data ?? []).filter((s) => s.group_id === props.groupId)),
  );
  const myAssignments = createMemo(() =>
    (assignments.data ?? []).filter((a) => a.group_id === props.groupId),
  );
  const myVenues = createMemo(() =>
    (venues.data ?? []).filter((v) => v.church_id === props.church?.id),
  );
  /** Person ids of the group members (base of the rotation). */
  const memberPersonIds = createMemo(() =>
    (members.data ?? []).flatMap((m) =>
      m.group_id === props.groupId && m.person_id ? [m.person_id] : [],
    ),
  );

  const personName = (id: string | null | undefined) =>
    props.people.find((p) => p.id === id)?.name ?? id ?? "";
  const venueName = (id: string | null | undefined) =>
    myVenues().find((v) => v.id === id)?.name ?? null;

  // ── Create shift ────────────────────────────────────────────────────────────
  const [shiftOpen, setShiftOpen] = createSignal(false);
  const [shiftTitle, setShiftTitle] = createSignal("");
  const [shiftDate, setShiftDate] = createSignal("");
  const [shiftVenue, setShiftVenue] = createSignal("");
  const addShift = async () => {
    if (!shiftTitle().trim()) {
      return;
    }
    await write()
      .insert(cleaning_shift)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        venue_id: shiftVenue() || null,
        title: shiftTitle().trim(),
        shift_date: shiftDate() || null,
        status: "pending",
        sort_order: myShifts().length + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setShiftOpen(false);
    setShiftTitle("");
    setShiftDate("");
    setShiftVenue("");
    showToast(t(msg`Cleaning shift created`), "success");
  };
  const trashShift = async (id: string) => {
    try {
      await patchRowVerified(write(), cleaning_shift, cleaning_shift.id, id, {
        deleted_at: nowIso(),
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };
  const toggleDone = async (s: CleaningShiftRecord) => {
    const done = isShiftDone(s);
    try {
      await patchRowVerified(write(), cleaning_shift, cleaning_shift.id, s.id, {
        status: done ? "pending" : "done",
        done_on: done ? null : nowIso().slice(0, 10),
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Assign person to a shift ────────────────────────────────────────────────
  const [assignShift, setAssignShift] = createSignal<string | null>(null);
  const [assignOpen, setAssignOpen] = createSignal(false);
  const [assignPerson, setAssignPerson] = createSignal("");
  const [assignArea, setAssignArea] = createSignal("");
  const openAssign = (shiftId: string) => {
    const suggested =
      nextInRotation(memberPersonIds(), myAssignments()) ?? props.people[0]?.id ?? "";
    setAssignShift(shiftId);
    setAssignPerson(suggested);
    setAssignArea("");
    setAssignOpen(true);
  };
  const addAssignment = async () => {
    const shiftId = assignShift();
    if (!shiftId || !assignPerson()) {
      return;
    }
    await write()
      .insert(cleaning_assignment)
      .values({
        id: `${shiftId}:${assignPerson()}`,
        church_id: props.church?.id,
        group_id: props.groupId,
        shift_id: shiftId,
        person_id: assignPerson(),
        area: assignArea().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setAssignOpen(false);
    showToast(t(msg`Person assigned`), "success");
  };
  const removeAssignment = async (id: string) => {
    await write().delete(cleaning_assignment).where(eq(cleaning_assignment.id, id));
  };

  // ── Automatic rotation: spreads members across pending shifts ───────────────
  const rotate = async () => {
    const pending = myShifts().filter((s) => !isShiftDone(s));
    const roster = rotateRoster(
      memberPersonIds(),
      pending.map((s) => s.id),
    );
    if (roster.length === 0) {
      showToast(t(msg`Add members and pending shifts first`), "error");
      return;
    }
    const writer = write();
    for (const { shiftId, personId } of roster) {
      const existing = assignmentsOfShift(myAssignments(), shiftId);
      if (existing.some((a) => a.person_id === personId)) {
        continue;
      }
      await writer.insert(cleaning_assignment).values({
        id: `${shiftId}:${personId}`,
        church_id: props.church?.id,
        group_id: props.groupId,
        shift_id: shiftId,
        person_id: personId,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    }
    showToast(t(msg`Roster rotated`), "success");
  };

  const memberOptions = createMemo(() =>
    memberPersonIds().map((id) => ({ value: id, label: personName(id) })),
  );

  return (
    <section class="space-y-2" data-testid="cleaning-section">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t(msg`Cleaning roster`)}
        </h3>
        <Show when={canWrite()}>
          <div class="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              data-testid="cleaning-rotate"
              onClick={() => void rotate()}
            >
              <RefreshCw size={13} /> {t(msg`Rotate`)}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="cleaning-shift-create"
              onClick={() => setShiftOpen(true)}
            >
              <Sparkles size={14} /> {t(msg`New shift`)}
            </Button>
          </div>
        </Show>
      </div>

      <Show
        when={myShifts().length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="cleaning-empty">
            {t(msg`No shifts yet.`)}
          </p>
        }
      >
        <For each={myShifts()}>
          {(shift) => {
            const rows = createMemo(() => assignmentsOfShift(myAssignments(), shift.id));
            return (
              <Card data-testid="cleaning-shift-item" data-shift-id={shift.id}>
                <CardContent class="space-y-2 py-3">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium">{shift.title}</p>
                      <p class="text-xs text-muted-foreground">
                        <Show when={shift.shift_date}>{shift.shift_date} · </Show>
                        <Show when={venueName(shift.venue_id)}>{venueName(shift.venue_id)} · </Show>
                        <span
                          data-testid="cleaning-shift-status"
                          data-status={isShiftDone(shift) ? "done" : "pending"}
                        >
                          {isShiftDone(shift) ? t(msg`Done`) : t(msg`Pending`)}
                        </span>
                      </p>
                    </div>
                    <Show when={canWrite()}>
                      <div class="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid="cleaning-done-toggle"
                          aria-label={isShiftDone(shift) ? t(msg`Mark pending`) : t(msg`Mark done`)}
                          onClick={() => void toggleDone(shift)}
                        >
                          <Show when={isShiftDone(shift)} fallback={<Circle size={15} />}>
                            <CheckCircle2 size={15} class="text-primary" />
                          </Show>
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid="cleaning-assign-open"
                          aria-label={t(msg`Assign person`)}
                          onClick={() => openAssign(shift.id)}
                        >
                          <UserPlus size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={t(msg`Move to trash`)}
                          onClick={() => void trashShift(shift.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </Show>
                  </div>

                  <Show
                    when={rows().length > 0}
                    fallback={
                      <p class="text-xs text-muted-foreground" data-testid="cleaning-uncovered">
                        {t(msg`Nobody assigned yet.`)}
                      </p>
                    }
                  >
                    <ul class="space-y-1 border-t border-border/60 pt-2">
                      <For each={rows()}>
                        {(row) => (
                          <li
                            class="flex items-center justify-between gap-2 text-sm"
                            data-testid="cleaning-assignment-item"
                            data-person-id={row.person_id}
                          >
                            <span class="min-w-0 truncate">
                              {row.person_name ?? personName(row.person_id)}
                              <Show when={row.area}>
                                <span class="text-muted-foreground"> · {row.area}</span>
                              </Show>
                            </span>
                            <Show when={canWrite()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Remove assignment`)}
                                onClick={() => void removeAssignment(row.id)}
                              >
                                <Trash2 size={12} />
                              </Button>
                            </Show>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </CardContent>
              </Card>
            );
          }}
        </For>
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={shiftOpen()}
        onOpenChange={setShiftOpen}
        title={t(msg`New cleaning shift`)}
        icon={<Sparkles size={20} />}
        submitTestId="cleaning-shift-submit"
        disabled={!shiftTitle().trim()}
        onSubmit={addShift}
      >
        <Field
          id="cs-title"
          label={t(msg`Title`)}
          value={shiftTitle()}
          onInput={setShiftTitle}
          testId="cleaning-shift-title-input"
        />
        <Field
          id="cs-date"
          label={t(msg`Date`)}
          type="date"
          value={shiftDate()}
          onInput={setShiftDate}
          testId="cleaning-shift-date-input"
        />
        <Show when={myVenues().length > 0}>
          <Field id="cs-venue" label={t(msg`Venue`)}>
            <Select
              options={[
                { value: "", label: t(msg`Any venue`) },
                ...myVenues().map((v) => ({ value: v.id, label: v.name ?? v.id })),
              ]}
              value={shiftVenue()}
              onChange={setShiftVenue}
              aria-label={t(msg`Venue`)}
            />
          </Field>
        </Show>
      </FormDialog>

      <FormDialog
        open={assignOpen()}
        onOpenChange={setAssignOpen}
        title={t(msg`Assign person to shift`)}
        icon={<UserPlus size={20} />}
        submitTestId="cleaning-assign-submit"
        disabled={!assignPerson()}
        onSubmit={addAssignment}
      >
        <Field id="ca-person" label={t(msg`Person`)}>
          <Select
            options={
              memberOptions().length > 0
                ? memberOptions()
                : props.people.map((p) => ({ value: p.id, label: p.name ?? p.id }))
            }
            value={assignPerson()}
            onChange={setAssignPerson}
            aria-label={t(msg`Person`)}
            emptyAction={{ label: t(msg`Add members to the group first`), href: "/people" }}
          />
        </Field>
        <Field
          id="ca-area"
          label={t(msg`Area (optional)`)}
          value={assignArea()}
          onInput={setAssignArea}
          testId="cleaning-assign-area-input"
        />
      </FormDialog>
    </section>
  );
}
